import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Building2, 
  User, 
  Target, 
  Brain, 
  Dumbbell, 
  Heart,
  BarChart3,
  Watch,
  FileText,
  Shield,
  TrendingUp,
  Users,
  ArrowRight,
  CheckCircle
} from "lucide-react";

const Programs = () => {
  const pillars = [
    { name: "Training", icon: Dumbbell, color: "#56C6C6" },
    { name: "Nutrition", icon: Heart, color: "#56C6C6" },
    { name: "Recovery", icon: Target, color: "#56C6C6" },
    { name: "Mindset", icon: Brain, color: "#56C6C6" }
  ];

  const orgFeatures = [
    {
      icon: Target,
      title: "Comprehensive Development Framework",
      description: "A complete athletic development framework centered around our Four Pillars: Training, Nutrition, Recovery, and Mindset. Every aspect of athlete performance is addressed systematically."
    },
    {
      icon: BarChart3,
      title: "Programming, Education & Analytics",
      description: "Structured training programming, in-depth education on all four pillars, and long-term data collection and analytics to drive continuous improvement across your entire organisation."
    },
    {
      icon: Watch,
      title: "Wearable Technology Included",
      description: "Garmin and WHOOP bands included with subscriptions for long-term data tracking on screenless devices. Gain powerful insights into recovery, performance, sleep quality, and more."
    },
    {
      icon: FileText,
      title: "Monthly Data Reports & Metrics",
      description: "Comprehensive data reports, testing metrics, and performance analytics tracked and reported monthly. The ultimate insight into athletic performance as a whole."
    },
    {
      icon: Shield,
      title: "Full Academy-Model Integration",
      description: "Complete integration into your organisation's structure, allowing V1 to oversee all athletic development and performance. Know your athletes are in expert hands with our academy model."
    },
    {
      icon: Users,
      title: "Scalable for Any Organisation",
      description: "Whether you have 20 athletes or 200, our framework scales to meet your needs while maintaining personalised attention and data-driven insights for every individual."
    }
  ];

  const individualFeatures = [
    {
      icon: Target,
      title: "Personalised Development Framework",
      description: "Access the same comprehensive Four Pillars framework used by elite organisations, tailored specifically to your sport, goals, and individual needs."
    },
    {
      icon: BarChart3,
      title: "Education & Sport-Specific Programming",
      description: "Receive structured training programming, in-depth education on Training, Nutrition, Recovery, and Mindset, plus sport-specific data analysis to optimise your performance."
    },
    {
      icon: Watch,
      title: "Wearable Device Options",
      description: "Options for Garmin and WHOOP wearable devices for continuous data tracking. Monitor your recovery, sleep, strain, and performance metrics around the clock."
    },
    {
      icon: TrendingUp,
      title: "Long-Term Performance Tracking",
      description: "Track your athletic journey with comprehensive data collection and analytics. See your progress over time and make informed decisions about your training."
    },
    {
      icon: FileText,
      title: "Regular Progress Reports",
      description: "Receive detailed reports on your testing metrics, performance data, and developmental progress to keep you informed and motivated."
    },
    {
      icon: Brain,
      title: "Holistic Athlete Development",
      description: "Go beyond just physical training. Develop mental resilience, optimal nutrition habits, and recovery protocols that support long-term athletic success."
    }
  ];

  return (
    <main data-testid="programs-page" className="pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[#56C6C6] font-mono text-sm tracking-widest uppercase mb-4">
                Our Programs
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight uppercase leading-tight mb-6">
                Elevate Your <span className="text-[#56C6C6]">Athletic Performance</span>
              </h1>
              <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
                Whether you're an organisation seeking comprehensive athletic development or an individual 
                athlete pursuing excellence, V1 has a program designed for you.
              </p>
            </motion.div>

            {/* Four Pillars Visual */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-12 flex flex-wrap justify-center gap-4 md:gap-8"
            >
              {pillars.map((pillar, index) => (
                <div key={pillar.name} className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-[#56C6C6]/10 border border-[#56C6C6]/20 flex items-center justify-center mb-3">
                    <pillar.icon className="w-8 h-8 text-[#56C6C6]" />
                  </div>
                  <span className="text-white font-semibold uppercase tracking-wider text-sm">{pillar.name}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Organisation Section */}
      <section id="organisations" className="py-20 bg-[#121212]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-4 mb-12"
          >
            <div className="w-14 h-14 bg-[#56C6C6]/10 border border-[#56C6C6]/30 flex items-center justify-center">
              <Building2 className="w-7 h-7 text-[#56C6C6]" />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight">
                V1 for Sports Organisations
              </h2>
              <p className="text-zinc-400 mt-1">Complete athletic development integration for teams and academies</p>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {orgFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-[#0A0A0A] border border-white/5 p-8 hover:border-[#56C6C6]/30 transition-all group"
              >
                <div className="w-12 h-12 bg-[#56C6C6]/10 flex items-center justify-center mb-6 group-hover:bg-[#56C6C6]/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-[#56C6C6]" />
                </div>
                <h3 className="text-xl font-bold text-white uppercase tracking-tight mb-4">
                  {feature.title}
                </h3>
                <p className="text-zinc-400 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Organisation CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-12 bg-gradient-to-r from-[#56C6C6]/10 to-transparent border border-[#56C6C6]/20 p-8 md:p-12"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-2xl font-bold text-white uppercase tracking-tight mb-2">
                  Ready to Transform Your Organisation?
                </h3>
                <p className="text-zinc-400">
                  Let V1 become your trusted partner in athletic development. Contact us to discuss your organisation's needs.
                </p>
              </div>
              <Link to="/contact">
                <Button 
                  data-testid="org-contact-btn"
                  className="bg-[#56C6C6] text-black hover:bg-[#45B5B5] font-bold uppercase tracking-wider px-8 py-6 whitespace-nowrap"
                >
                  Get in Touch
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Individual Athletes Section */}
      <section id="individuals" className="py-20 bg-[#0A0A0A]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-4 mb-12"
          >
            <div className="w-14 h-14 bg-[#56C6C6]/10 border border-[#56C6C6]/30 flex items-center justify-center">
              <User className="w-7 h-7 text-[#56C6C6]" />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight">
                V1 for Individual Athletes
              </h2>
              <p className="text-zinc-400 mt-1">Personalised development programs for athletes pursuing excellence</p>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {individualFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-[#121212] border border-white/5 p-8 hover:border-[#56C6C6]/30 transition-all group"
              >
                <div className="w-12 h-12 bg-[#56C6C6]/10 flex items-center justify-center mb-6 group-hover:bg-[#56C6C6]/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-[#56C6C6]" />
                </div>
                <h3 className="text-xl font-bold text-white uppercase tracking-tight mb-4">
                  {feature.title}
                </h3>
                <p className="text-zinc-400 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* What's Included Highlight */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <div className="bg-[#121212] border border-white/5 p-8">
              <h4 className="text-lg font-bold text-white uppercase tracking-tight mb-6 flex items-center gap-3">
                <Watch className="w-5 h-5 text-[#56C6C6]" />
                Wearable Options
              </h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#56C6C6] flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-white font-semibold">Garmin Devices</span>
                    <p className="text-zinc-400 text-sm mt-1">Advanced fitness tracking with GPS, heart rate monitoring, and performance metrics</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#56C6C6] flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-white font-semibold">WHOOP Bands</span>
                    <p className="text-zinc-400 text-sm mt-1">24/7 health and fitness monitoring with recovery, strain, and sleep tracking</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-[#121212] border border-white/5 p-8">
              <h4 className="text-lg font-bold text-white uppercase tracking-tight mb-6 flex items-center gap-3">
                <BarChart3 className="w-5 h-5 text-[#56C6C6]" />
                Data & Insights
              </h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#56C6C6] flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-white font-semibold">Sport-Specific Analytics</span>
                    <p className="text-zinc-400 text-sm mt-1">Data analysis tailored to your sport's unique demands and performance indicators</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#56C6C6] flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-white font-semibold">Progress Tracking</span>
                    <p className="text-zinc-400 text-sm mt-1">Long-term performance data to visualise your growth and identify areas for improvement</p>
                  </div>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Individual CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-12 bg-gradient-to-r from-[#56C6C6]/10 to-transparent border border-[#56C6C6]/20 p-8 md:p-12"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-2xl font-bold text-white uppercase tracking-tight mb-2">
                  Start Your Athletic Journey Today
                </h3>
                <p className="text-zinc-400">
                  Take the first step towards reaching your full potential. Connect with V1 to learn more about our individual programs.
                </p>
              </div>
              <Link to="/contact">
                <Button 
                  data-testid="individual-contact-btn"
                  className="bg-[#56C6C6] text-black hover:bg-[#45B5B5] font-bold uppercase tracking-wider px-8 py-6 whitespace-nowrap"
                >
                  Contact Us
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Programs;
